import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema, FAQPageSchema } from '@/components/base/SchemaOrg';
import { aboutData, faqData } from '@/mocks/about';

export default function AboutPage() {
  const { brandIntro, reputation, contact } = aboutData;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitted(true);
    setFormError('');
  };

  return (
    <>
      <ScrollToTop />
      <title>关于我们 - 海角社区视频 | 品牌介绍与联系方式</title>
      <meta name="description" content="了解海角社区视频的品牌故事、发展历程和核心价值。海角社区视频汇聚海量优质内容，覆盖娱乐、音乐、美食、旅行、科技等领域，致力于打造最具活力的中文视频社区平台。" />
      <meta name="keywords" content="海角社区视频,关于我们,海角社区视频品牌,海角社区视频联系方式,社区视频平台" />
      <link rel="canonical" href="https://haijiao.shipin.com/about" />
      <meta property="og:title" content="关于我们 - 海角社区视频 | 品牌介绍与联系方式" />
      <meta property="og:description" content="了解海角社区视频的品牌故事、发展历程和核心价值。海角社区视频汇聚海量优质内容，覆盖娱乐、音乐、美食、旅行、科技等领域。" />
      <meta property="og:url" content="https://haijiao.shipin.com/about" />
      <meta property="og:type" content="website" />
      <WebPageSchema
        title="关于我们 - 海角社区视频"
        description="了解海角社区视频的品牌故事、发展历程和核心价值。海角社区视频汇聚海量优质内容，覆盖多个领域，致力于打造最具活力的中文视频社区平台。"
        url="https://haijiao.shipin.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://haijiao.shipin.com' },
          { name: '关于我们', url: 'https://haijiao.shipin.com/about' },
        ]}
      />
      <FAQPageSchema questions={faqData} />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb />
        </div>

        {/* Brand Intro Section */}
        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-play-circle-fill text-primary-600 text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-3">{brandIntro.title}</h1>
            <p className="text-lg text-foreground-500 mb-8">{brandIntro.subtitle}</p>
            <p className="text-foreground-600 leading-relaxed max-w-3xl mx-auto mb-10">
              {brandIntro.description}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {brandIntro.stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-5">
                  <p className="text-2xl md:text-3xl font-bold text-primary-600">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-flag-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-foreground-600 leading-relaxed text-sm">{brandIntro.mission}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-foreground-600 leading-relaxed text-sm">{brandIntro.vision}</p>
              </div>
            </div>

            {/* Values */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-foreground-900 mb-5 text-center">核心价值观</h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {brandIntro.values.map((value) => (
                  <span key={value} className="px-5 py-2.5 rounded-full bg-background-50 border border-background-200 text-sm font-medium text-foreground-700">
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation Section */}
        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">{reputation.title}</h2>
              <p className="text-foreground-500">{reputation.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reputation.testimonials.map((item) => (
                <div key={item.id} className="bg-background-100 rounded-2xl p-6 border border-background-200">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={item.avatar} alt={item.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" loading="lazy" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-400">{item.role}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${i < item.rating ? 'text-amber-400' : 'text-foreground-200'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact & FAQ Section */}
        <section className="w-full bg-background-100 py-10 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">{contact.title}</h2>
                <p className="text-sm text-foreground-500 mb-6">{contact.subtitle}</p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">客服电话</p>
                      <p className="text-sm font-medium text-foreground-900">{contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900">{contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">工作时间</p>
                      <p className="text-sm font-medium text-foreground-900">{contact.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-foreground-400 mb-3">关注我们</p>
                  <div className="flex items-center gap-3">
                    {contact.socialLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.url}
                        className="w-10 h-10 rounded-full bg-background-50 border border-background-200 flex items-center justify-center hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
                        aria-label={link.name}
                        rel="nofollow"
                      >
                        <i className={`${link.icon} text-foreground-600 text-sm`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200">
                  <h3 className="text-lg font-bold text-foreground-900 mb-1">发送消息</h3>
                  <p className="text-xs text-foreground-400 mb-5">有任何问题或建议，欢迎给我们留言</p>

                  {formSubmitted ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-check-line text-accent-600 text-2xl"></i>
                      </div>
                      <p className="text-foreground-900 font-medium mb-1">消息已发送！</p>
                      <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复</p>
                      <button
                        onClick={() => setFormSubmitted(false)}
                        className="mt-5 text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        发送新消息
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {formError && (
                        <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl">{formError}</div>
                      )}
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="你的名字"
                          required
                          className="w-full bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-sm text-foreground-700 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="电子邮箱"
                          required
                          className="w-full bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-sm text-foreground-700 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入你的留言内容..."
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-sm text-foreground-700 placeholder-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">常见问题</h2>
              <p className="text-foreground-500">关于海角社区视频的常见疑问解答</p>
            </div>

            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h4 className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-background-50 pb-14 md:pb-20">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              加入海角社区视频创作者社群
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              无论你是什么领域的创作者，海角社区视频都为你提供展示才华的舞台
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line mr-2"></i>
              开始浏览精彩内容
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}